import React, { useState, useCallback } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { UserCard, Typography, BottomActions, Button } from "src/components";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useUser, useCognito, useCall, useReviewActions } from "src/hooks";
import { RatingInput, CommentInput } from "./components";
import { v4 as uuid } from "uuid";
import LocalText from "src/local";
import { RootStackScreenProps } from "src/navigation/types";

export type ReviewNavigationParam = {
  callId: string;
  showHistory?: boolean;
};

export const ReviewScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"Review">["navigation"]>();
  const route = useRoute<RootStackScreenProps<"Review">["route"]>();
  const [cognitoUsername] = useCognito();
  const [call] = useCall(route.params.callId);
  const [user] = useUser(
    call?.senderId === cognitoUsername ? call?.opponentId : call?.senderId
  );
  const [rating, setRating] = useState<number>(0);
  const [comment, setComment] = useState<string>("");
  const { createReview } = useReviewActions();
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const postHandler = useCallback(async () => {
    setActionLoading(true);
    try {
      await createReview({
        input: {
          id: uuid() as string,
          requestId: route.params.callId,
          rate: rating,
          comment: comment.length > 0 ? comment : undefined,
          userId:
            call?.senderId !== cognitoUsername ? call?.senderId : undefined,
          serviceId:
            call?.senderId === cognitoUsername ? call?.serviceId : undefined,
        },
      });
      navigation.goBack();
      setTimeout(
        () =>
          navigation.navigate("CallHistory", { callId: route.params.callId }),
        1000
      );
    } catch (e) {
      console.error(e);
    }
    setActionLoading(false);
  }, [call, cognitoUsername, rating, comment, navigation]);
  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        {user ? (
          <UserCard
            contanerStyle={styles.user}
            source={user?.image ? { uri: user?.image } : undefined}
            status={user?.status}
            name={user?.name}
            birthday={user?.birthday}
            gender={user?.gender}
            prefecture={user?.address?.prefecture}
            isVerified={user?.identificationStatus === "APPROVED"}
            onPress={() => navigation.push("User", { id: user.id, title: "" })}
          />
        ) : null}
        <Typography
          text={LocalText.screens.review.ratingSection}
          variant={"section"}
        />
        <RatingInput
          containerStyle={styles.rating}
          value={rating}
          onChange={setRating}
        />
        <CommentInput
          containerStyle={styles.comment}
          value={comment}
          onChangeText={setComment}
        />
      </ScrollView>
      <BottomActions>
        <Button
          variant={"primary"}
          icon={"post"}
          title={LocalText.screens.review.post}
          disabled={rating === 0}
          loading={actionLoading}
          onPress={postHandler}
        />
      </BottomActions>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  user: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  rating: {
    marginTop: 8,
    marginHorizontal: 16,
  },
  comment: {
    marginTop: 38,
  },
});
